import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Upload, Plus, Trash2, Search, UserPlus, UserMinus, Check, X,
  Send, Loader2, MessageCircle, Users, Ban, UserCircle, Star,
} from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { DigitalBackground } from "@/components/DigitalBackground";
import { toast } from "sonner";
import { SafetyWarningModal } from "@/components/SafetyWarningModal";

const TABS = [
  { key: "profile", label: "Profile", icon: UserCircle },
  { key: "friends", label: "Friends", icon: Users },
  { key: "messages", label: "Messages", icon: MessageCircle },
  { key: "blocked", label: "Blocked", icon: Ban },
];

const GRADIENT_PRESETS = [
  ["#22d3ee", "#a855f7"],
  ["#ec4899", "#f59e0b"],
  ["#3b82f6", "#22c55e"],
  ["#8b5cf6", "#ec4899"],
  ["#06b6d4", "#3b82f6"],
  ["#ef4444", "#8b5cf6"],
];

const hexLum = (hex) => {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return 0;
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

export default function Profile() {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("dm") ? "messages" : "profile");

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black text-white font-body">
      <SafetyWarningModal />
      <DigitalBackground />
      <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-400 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to site
        </button>

        <div className="mt-5 flex items-center gap-4">
          <Avatar path={user.avatar_path} name={user.nickname || user.name} size="lg" />
          <div>
            <h1 className="font-heading font-black text-2xl sm:text-3xl text-gradient-accent">
              {user.nickname || "Your Profile"}
            </h1>
            <p className="text-sm text-slate-400">{user.email}</p>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-white/10 pb-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`profile-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 rounded-t-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                  tab === t.key ? "text-cyan-400 border-b-2 border-cyan-400" : "text-slate-400 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          {tab === "profile" && <ProfileEditor user={user} refreshUser={refreshUser} />}
          {tab === "friends" && <FriendsTab onDM={(id) => { setTab("messages"); navigate(`/profile?dm=${id}`); }} />}
          {tab === "messages" && <MessagesTab initialDm={params.get("dm")} />}
          {tab === "blocked" && <BlockedTab />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Profile editor
function ProfileEditor({ user, refreshUser }) {
  const [nickname, setNickname] = useState(user.nickname || "");
  const [bio, setBio] = useState(user.bio || "");
  const [links, setLinks] = useState(user.links?.length ? user.links : []);
  const [color, setColor] = useState(user.color || "#22d3ee");
  const [color2, setColor2] = useState(user.color2 || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const addLink = () => setLinks([...links, { label: "", url: "" }]);
  const updateLink = (i, k, v) => setLinks(links.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const removeLink = (i) => setLinks(links.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const clean = links.filter((l) => l.label.trim() && l.url.trim());
      await api.put("/profile", { nickname, bio, links: clean, color, color2: color2 || null });
      await refreshUser();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={save} className="electric-card rounded-2xl bg-black/50 backdrop-blur-xl border border-white/10 p-6" data-testid="profile-editor">
      <div className="flex items-center gap-5">
        <Avatar path={user.avatar_path} name={nickname || user.name} size="lg" />
        <div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="avatar-file-input" />
          <button
            type="button"
            data-testid="avatar-upload-button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn-ghost-electric inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-semibold text-white"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Upload image"}
          </button>
          <p className="mt-2 text-xs text-slate-500">JPG / PNG / WEBP, max 5MB</p>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-slate-400">Nickname</label>
          <input
            data-testid="profile-nickname-input"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            required
            minLength={2}
            placeholder="Your display name"
            className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none"
          />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-slate-400">Bio</label>
          <textarea
            data-testid="profile-bio-input"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={3}
            placeholder="Tell the community about you and your projects"
            className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none resize-none"
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-mono uppercase tracking-wider text-slate-400">Project Links</label>
            <button type="button" onClick={addLink} data-testid="add-link-button" className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:underline">
              <Plus className="w-3.5 h-3.5" /> Add link
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {links.map((l, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={l.label}
                  onChange={(e) => updateLink(i, "label", e.target.value)}
                  placeholder="Label"
                  className="w-1/3 rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none"
                />
                <input
                  value={l.url}
                  onChange={(e) => updateLink(i, "url", e.target.value)}
                  placeholder="https://…"
                  className="flex-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none"
                />
                <button type="button" onClick={() => removeLink(i)} className="text-slate-500 hover:text-red-400 px-2">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-slate-400">Chat Bubble Gradient</label>
          <p className="mt-1 text-xs text-slate-500">Pick two colors for a left-to-right fade. Leave the second empty for a solid color. Text auto-adjusts to stay readable.</p>

          {(() => {
            const c2 = color2 || color;
            const avgLum = (hexLum(color) + hexLum(c2)) / 2;
            const txt = avgLum > 0.62 ? "#0a0a0a" : "#ffffff";
            const needsScrim = avgLum >= 0.35 && avgLum <= 0.82;
            const scrim = txt === "#ffffff" ? "rgba(0,0,0,0.28)" : "rgba(255,255,255,0.4)";
            return (
              <>
                <div className="mt-3 flex items-end gap-4 flex-wrap">
                  <div className="flex flex-col items-center gap-1">
                    <input
                      type="color"
                      data-testid="profile-color-input"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="w-14 h-14 rounded-xl bg-transparent border border-white/15 cursor-pointer p-0"
                      title="Color A"
                    />
                    <span className="text-[10px] text-slate-500 font-mono">Color A</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <input
                      type="color"
                      data-testid="profile-color2-input"
                      value={color2 || "#a855f7"}
                      onChange={(e) => setColor2(e.target.value)}
                      className="w-14 h-14 rounded-xl bg-transparent border border-white/15 cursor-pointer p-0"
                      title="Color B"
                    />
                    <span className="text-[10px] text-slate-500 font-mono">Color B</span>
                  </div>
                  {color2 && (
                    <button
                      type="button"
                      data-testid="profile-color2-clear"
                      onClick={() => setColor2("")}
                      className="mb-5 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-400 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" /> solid
                    </button>
                  )}
                  <div
                    data-testid="profile-color-preview"
                    className="relative overflow-hidden rounded-2xl rounded-tr-md px-4 py-2.5 text-sm font-medium shadow-lg flex-1 min-w-[180px]"
                    style={{ backgroundImage: `linear-gradient(90deg, ${color}, ${c2})` }}
                  >
                    {needsScrim && <span aria-hidden className="absolute inset-0" style={{ background: scrim }} />}
                    <span className="relative" style={{ color: txt }}>Your message preview</span>
                  </div>
                </div>

                <div className="mt-3">
                  <span className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">Gradient presets</span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {GRADIENT_PRESETS.map(([a, b]) => (
                      <button
                        key={a + b}
                        type="button"
                        data-testid={`gradient-preset-${a.replace('#','')}-${b.replace('#','')}`}
                        onClick={() => { setColor(a); setColor2(b); }}
                        className={`h-8 w-14 rounded-full border-2 transition-transform hover:scale-105 ${color.toLowerCase() === a && color2.toLowerCase() === b ? "border-white" : "border-white/20"}`}
                        style={{ backgroundImage: `linear-gradient(90deg, ${a}, ${b})` }}
                        aria-label={`${a} to ${b}`}
                      />
                    ))}
                  </div>
                </div>
              </>
            );
          })()}
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="profile-save-button"
          className="btn-electric text-black font-bold text-sm px-6 py-3 rounded-xl disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save Profile"}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------- Friends
function FriendsTab({ onDM }) {
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const load = useCallback(() => {
    api.get("/friends").then((r) => setFriends(r.data)).catch(() => {});
    api.get("/friends/requests").then((r) => setRequests(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const doSearch = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
      setResults(data);
    } finally {
      setSearching(false);
    }
  };

  const action = async (fn) => {
    await fn();
    load();
    if (q.trim()) {
      const { data } = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
      setResults(data);
    }
  };

  const Row = ({ p, children }) => (
    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/40 p-3">
      <Avatar path={p.avatar_path} name={p.nickname} size="sm" online={p.online} />
      <span className="flex-1 min-w-0 flex items-center gap-1">
        <span className="font-semibold text-sm text-white truncate">{p.nickname}</span>
        {p.is_admin && <Star data-testid={`admin-star-${p.id}`} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400 shrink-0" />}
      </span>
      <div className="flex gap-2">{children}</div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="friends-tab">
      <form onSubmit={doSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="friend-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search members by nickname or email"
            className="w-full rounded-xl bg-white/5 border border-white/10 pl-10 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none"
          />
        </div>
        <button type="submit" className="btn-electric text-black font-bold text-sm px-5 rounded-xl">
          {searching ? "…" : "Search"}
        </button>
      </form>

      {results !== null && (
        <div data-testid="search-results">
          <h3 className="font-heading font-bold text-white text-sm mb-3">Search results</h3>
          <div className="space-y-2">
            {results.length === 0 ? (
              <p className="text-sm text-slate-500">No members found.</p>
            ) : (
              results.map((p) => (
                <Row key={p.id} p={p}>
                  {p.is_blocked ? (
                    <button onClick={() => action(() => api.delete(`/block/${p.id}`))} className="text-xs text-slate-300 border border-white/15 rounded-full px-3 py-1.5">Unblock</button>
                  ) : p.is_friend ? (
                    <span className="text-xs text-cyan-400 px-2 py-1.5">Friends</span>
                  ) : p.request_received ? (
                    <button data-testid={`accept-${p.id}`} onClick={() => action(() => api.post(`/friends/accept/${p.id}`))} className="btn-electric text-black text-xs font-bold rounded-full px-3 py-1.5">Accept</button>
                  ) : p.request_sent ? (
                    <span className="text-xs text-slate-500 px-2 py-1.5">Requested</span>
                  ) : (
                    <button data-testid={`addfriend-${p.id}`} onClick={() => action(() => api.post(`/friends/request/${p.id}`))} className="inline-flex items-center gap-1 text-xs text-white border border-white/15 rounded-full px-3 py-1.5 hover:border-cyan-400/50"><UserPlus className="w-3.5 h-3.5" /> Add</button>
                  )}
                  {!p.is_blocked && (
                    <button onClick={() => action(() => api.post(`/block/${p.id}`))} className="text-xs text-slate-500 hover:text-red-400 px-2 py-1.5">Block</button>
                  )}
                </Row>
              ))
            )}
          </div>
        </div>
      )}

      {requests.length > 0 && (
        <div data-testid="friend-requests">
          <h3 className="font-heading font-bold text-white text-sm mb-3">Friend requests ({requests.length})</h3>
          <div className="space-y-2">
            {requests.map((p) => (
              <Row key={p.id} p={p}>
                <button data-testid={`req-accept-${p.id}`} onClick={() => action(() => api.post(`/friends/accept/${p.id}`))} className="btn-electric text-black text-xs font-bold rounded-full px-3 py-1.5"><Check className="w-3.5 h-3.5" /></button>
                <button onClick={() => action(() => api.post(`/friends/decline/${p.id}`))} className="text-xs text-slate-400 border border-white/15 rounded-full px-3 py-1.5"><X className="w-3.5 h-3.5" /></button>
              </Row>
            ))}
          </div>
        </div>
      )}

      <div data-testid="friends-list">
        <h3 className="font-heading font-bold text-white text-sm mb-3">Your friends ({friends.length})</h3>
        <div className="space-y-2">
          {friends.length === 0 ? (
            <p className="text-sm text-slate-500">No friends yet. Search for members above.</p>
          ) : (
            friends.map((p) => (
              <Row key={p.id} p={p}>
                <button onClick={() => onDM(p.id)} className="inline-flex items-center gap-1 text-xs text-cyan-400 border border-white/15 rounded-full px-3 py-1.5"><MessageCircle className="w-3.5 h-3.5" /> Message</button>
                <button onClick={() => action(() => api.delete(`/friends/${p.id}`))} className="text-xs text-slate-500 hover:text-red-400 px-2 py-1.5"><UserMinus className="w-3.5 h-3.5" /></button>
              </Row>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Blocked
function BlockedTab() {
  const [blocks, setBlocks] = useState([]);
  const load = useCallback(() => { api.get("/blocks").then((r) => setBlocks(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="blocked-tab">
      <h3 className="font-heading font-bold text-white text-sm mb-3">Blocked users ({blocks.length})</h3>
      <div className="space-y-2">
        {blocks.length === 0 ? (
          <p className="text-sm text-slate-500">You haven't blocked anyone.</p>
        ) : (
          blocks.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/40 p-3">
              <Avatar path={p.avatar_path} name={p.nickname} size="sm" />
              <span className="flex-1 font-semibold text-sm text-white truncate">{p.nickname}</span>
              <button
                data-testid={`unblock-${p.id}`}
                onClick={async () => { await api.delete(`/block/${p.id}`); load(); }}
                className="text-xs text-white border border-white/15 rounded-full px-4 py-1.5 hover:border-cyan-400/50"
              >
                Unblock
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Messages (DM, polling)
function MessagesTab({ initialDm }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(initialDm || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const loadThreads = useCallback(() => { api.get("/dm/threads").then((r) => setThreads(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    if (!active) return;
    let stop = false;
    const poll = () => api.get(`/dm/${active}`).then((r) => { if (!stop) setMessages(r.data); }).catch(() => {});
    poll();
    const id = setInterval(poll, 3000);
    return () => { stop = true; clearInterval(id); };
  }, [active]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/dm/${active}`, { content: text });
      setMessages((m) => [...m, data]);
      setText("");
      loadThreads();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const activeThread = threads.find((t) => t.id === active);

  return (
    <div className="grid sm:grid-cols-[220px_1fr] gap-4" data-testid="messages-tab">
      <div className="space-y-2">
        <h3 className="font-heading font-bold text-white text-sm">Conversations</h3>
        {threads.length === 0 ? (
          <p className="text-xs text-slate-500">No messages yet. Start one from a friend's profile.</p>
        ) : (
          threads.map((t) => (
            <button
              key={t.id}
              data-testid={`dm-thread-${t.id}`}
              onClick={() => setActive(t.id)}
              className={`w-full flex items-center gap-2 rounded-xl border p-2.5 text-left transition-colors ${
                active === t.id ? "border-cyan-400/50 bg-cyan-500/10" : "border-white/10 bg-black/40 hover:bg-white/5"
              }`}
            >
              <Avatar path={t.avatar_path} name={t.nickname} size="sm" online={t.online} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate flex items-center gap-1">
                  {t.nickname}
                  {t.is_admin && <Star data-testid={`dm-thread-star-${t.id}`} className="w-3 h-3 text-yellow-400 fill-yellow-400 shrink-0" />}
                </p>
                <p className="text-xs text-slate-500 truncate">{t.last_message}</p>
              </div>
            </button>
          ))
        )}
      </div>

      <div className="electric-card rounded-2xl bg-black/50 backdrop-blur-xl border border-white/10 flex flex-col min-h-[420px]">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
            Select a conversation
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-white/10 font-semibold text-sm text-white flex items-center gap-1.5">
              {activeThread?.nickname || "Chat"}
              {activeThread?.is_admin && <Star data-testid="dm-header-star" className="w-4 h-4 text-yellow-400 fill-yellow-400" />}
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[380px]">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                    m.mine ? "bg-gradient-to-br from-cyan-500 to-purple-600 text-black font-medium" : "bg-white/10 text-slate-100"
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
              <input
                data-testid="dm-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message…"
                className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none"
              />
              <button type="submit" data-testid="dm-send" className="btn-electric text-black rounded-full w-11 h-11 flex items-center justify-center">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
